import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/daf7e2ptto0ceqqnh720';

type Status = 'idle' | 'success' | 'error';

export default function Newsletter() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;

    const hp = form.elements.namedItem('website_alt') as HTMLInputElement | null;
    if (hp && hp.value.trim()) {
      // 命中反垃圾陷阱：不真正提交，仅展示与正常一致的成功反馈
      setStatus('success');
      setMessage('订阅成功！每日早报将准时送到你的邮箱。');
      form.reset();
      return;
    }

    const fd = new FormData(form);
    const emailVal = (form.elements.namedItem('email') as HTMLInputElement | null)?.value.trim() ?? '';
    setEmail(emailVal);
    setStatus('idle');
    setMessage('');

    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => {
        const v = typeof value === 'string' ? value : '';
        if (key === 'website_alt') return;
        if (v.trim()) body.append(key, v);
      });

      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (responseText && parsed === null ? responseText : '');

      if (res.ok && parsed && parsed.code === 'OK') {
        setStatus('success');
        setMessage('订阅成功！明早的晨间瓜速递将准时送到你的邮箱。');
        form.reset();
        setEmail('');
      } else {
        setStatus('error');
        setMessage(
          serverMsg && serverMsg.toLowerCase().includes('spam')
            ? '提交过于频繁，请稍后再试。'
            : serverMsg || '订阅未成功，请检查邮箱后重试。'
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络开小差了，请稍后再试。');
    }
  }

  return (
    <section id="subscribe" className="mx-auto max-w-7xl scroll-mt-24 px-4 pb-16 md:px-6 md:pb-24">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-secondary-500 via-primary-500 to-primary-600 px-6 py-12 md:px-12 md:py-16">
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-accent-400/30 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-16 left-1/4 h-52 w-52 rounded-full bg-foreground-950/10 blur-2xl" />

        <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground-950/20 px-3 py-1 text-xs font-semibold text-background-50 backdrop-blur">
              <i className="ri-mail-send-line h-3.5 w-3.5" />
              每日早报 · 完全免费
            </span>
            <h2 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-4xl">
              让明早的第一口瓜，<br />
              主动来敲你的门
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-background-100 md:text-base">
              留下邮箱，订阅晨间瓜速递。每天清晨第一时间收到当日的头条瓜与首发猛料，不翻热搜也能追完今日进度。
            </p>
          </div>

          <div className="w-full">
            <form
              id="daily-newsletter"
              data-readdy-form="daily-newsletter"
              onSubmit={handleSubmit}
              className="rounded-2xl bg-background-50 p-5 md:p-6"
              noValidate
            >
              <div className="flex flex-col gap-3 sm:flex-row">
                <label htmlFor="newsletter-email" className="sr-only">
                  订阅邮箱
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入你的邮箱，如 name@example.com"
                  className="h-12 flex-1 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
                <button
                  type="submit"
                  className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  立即订阅
                  <i className="ri-send-plane-2-fill ml-2 h-4 w-4" />
                </button>
              </div>

              <div className="hp-wrap" aria-hidden="true">
                <label>
                  邮箱确认
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>
              </div>

              <p className="mt-3 flex items-start gap-1.5 text-xs text-foreground-500">
                <i className="ri-lock-line mt-0.5 h-3.5 w-3.5 shrink-0" />
                我们仅用于发送每日早报，可随时一键退订，不会用于任何其他用途。
              </p>

              {status === 'success' ? (
                <p className="mt-3 flex items-center gap-2 rounded-lg bg-accent-100 px-3 py-2.5 text-sm font-medium text-accent-900">
                  <i className="ri-checkbox-circle-fill h-4 w-4" />
                  {message}
                </p>
              ) : null}
              {status === 'error' ? (
                <p className="mt-3 flex items-center gap-2 rounded-lg bg-primary-100 px-3 py-2.5 text-sm font-medium text-primary-800">
                  <i className="ri-error-warning-fill h-4 w-4" />
                  {message}
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}